import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Phone, ChevronRight } from 'lucide-react';
import { api, Patient } from '../lib/api';

export function PatientsPage() {
    const [patients, setPatients] = useState<Patient[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        loadPatients();
    }, [search]);

    async function loadPatients() {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set('q', search);
            const res = await api.patients.list(params.toString());
            setPatients(res.data);
        } catch {
            // Use demo data if API isn't running
            setPatients([{
                id: '1', fhirId: 'demo', clinicPatientId: 'CLI-2026-0001',
                firstName: 'María', lastName: 'García', dateOfBirth: '1985-06-15',
                gender: 'female', mobilePhone: '+573001234567', isActive: true,
                createdAt: new Date().toISOString(),
            }]);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="page-content fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
                <h1 className="page-title">Pacientes</h1>
                <button className="btn btn-primary" onClick={() => setShowNew(true)} id="new-patient-btn">
                    <Plus size={16} /> Nuevo paciente
                </button>
            </div>

            {/* Search Bar */}
            <div style={{ position: 'relative', marginBottom: 'var(--space-6)' }}>
                <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
                <input
                    type="text"
                    className="form-input"
                    placeholder="Buscar por nombre, teléfono, o cédula..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: '100%', paddingLeft: 40 }}
                    id="patient-search"
                />
            </div>

            {/* Patient Table */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-container" style={{ border: 'none' }}>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>ID Clínica</th>
                                <th>Nombre</th>
                                <th>Teléfono</th>
                                <th>Cédula</th>
                                <th>Fecha de Nacimiento</th>
                                <th>Estado</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i}>
                                        {Array.from({ length: 7 }).map((_, j) => (
                                            <td key={j}><div className="skeleton" style={{ height: 20, width: '80%' }} /></td>
                                        ))}
                                    </tr>
                                ))
                            ) : patients.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-text-muted)' }}>
                                        No se encontraron pacientes
                                    </td>
                                </tr>
                            ) : (
                                patients.map((p) => (
                                    <tr key={p.id} onClick={() => navigate(`/patients/${p.id}`)} style={{ cursor: 'pointer' }}>
                                        <td><span className="badge badge-primary">{p.clinicPatientId}</span></td>
                                        <td style={{ fontWeight: 500 }}>{p.firstName} {p.lastName}</td>
                                        <td>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <Phone size={14} color="var(--color-text-muted)" />
                                                {p.mobilePhone}
                                            </span>
                                        </td>
                                        <td>{p.cedula ?? '—'}</td>
                                        <td>{new Date(p.dateOfBirth).toLocaleDateString('es-CO')}</td>
                                        <td>
                                            <span className={`badge ${p.isActive ? 'badge-success' : 'badge-neutral'}`}>
                                                {p.isActive ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td><ChevronRight size={16} color="var(--color-text-muted)" /></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* New Patient Modal */}
            {showNew && (
                <div className="modal-overlay" onClick={() => setShowNew(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Nuevo Paciente</h2>
                            <button className="btn btn-icon" onClick={() => setShowNew(false)}>✕</button>
                        </div>
                        <form onSubmit={async (e) => {
                            e.preventDefault();
                            const form = new FormData(e.currentTarget);
                            try {
                                await api.patients.create({
                                    firstName: form.get('firstName') as string,
                                    lastName: form.get('lastName') as string,
                                    dateOfBirth: form.get('dateOfBirth') as string,
                                    mobilePhone: form.get('mobilePhone') as string,
                                    cedula: form.get('cedula') as string || undefined,
                                    email: form.get('email') as string || undefined,
                                    gender: form.get('gender') as string || undefined,
                                });
                                setShowNew(false);
                                loadPatients();
                            } catch (err) {
                                alert(err instanceof Error ? err.message : 'Error al crear paciente');
                            }
                        }}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Nombre *</label>
                                    <input className="form-input" name="firstName" required placeholder="María" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Apellido *</label>
                                    <input className="form-input" name="lastName" required placeholder="García" />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Fecha de nacimiento *</label>
                                    <input className="form-input" type="date" name="dateOfBirth" required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Género</label>
                                    <select className="form-input" name="gender">
                                        <option value="">Seleccionar</option>
                                        <option value="male">Masculino</option>
                                        <option value="female">Femenino</option>
                                        <option value="other">Otro</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Teléfono móvil *</label>
                                    <input className="form-input" name="mobilePhone" required placeholder="+573001234567" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Cédula</label>
                                    <input className="form-input" name="cedula" placeholder="1234567890" />
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Correo electrónico</label>
                                <input className="form-input" type="email" name="email" placeholder="paciente@ejemplo.com" />
                            </div>
                            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', marginTop: 'var(--space-4)' }}>
                                <button type="button" className="btn btn-secondary" onClick={() => setShowNew(false)}>Cancelar</button>
                                <button type="submit" className="btn btn-primary">Crear paciente</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
